import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class RecipeQcTestMetricExpectationReadOnly extends ModelObject<RecipeQcTestMetricExpectationReadOnly> {

    /**
    * Either old-style Assembly RecipeId OR new-style Batch RecipeId which has expectations for the given qc test metric.
    */
    public recipeId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.recipeId = Guid.parseOrEmpty(jsonSerializedObject.recipeId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.recipeId = this.recipeId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "recipeId", rule: (v: any) => !v.isEmpty() || "RecipeQcTestMetricExpectationReadOnly.RecipeId cannot be empty"},
    ];
}


export class RecipeQcTestMetricExpectation extends ModelObject<RecipeQcTestMetricExpectation> {

    /**
    * Qc Test metric line to which the expectations relate.
    */
    public qcTestMetricLineId: Guid = Guid.createEmpty();
    private _expectedEndValue: number | null = null;
    /**
    * The value the metric is expected to have at the end of testing within an assembly.
    */
    public get expectedEndValue(): number | null { return this._expectedEndValue }
    public set expectedEndValue(value: number | null) {
      this._expectedEndValue = value === undefined ? null : value;
    }
    private _expectedStartValue: number | null = null;
    /**
    * The value the metric is expected to have at the start of testing within an assembly.
    */
    public get expectedStartValue(): number | null { return this._expectedStartValue }
    public set expectedStartValue(value: number | null) {
      this._expectedStartValue = value === undefined ? null : value;
    }
    private _maximumExpectedValue: number | null = null;
    /**
    * The maximum value the metric is expected to have during an assembly.
    */
    public get maximumExpectedValue(): number | null { return this._maximumExpectedValue }
    public set maximumExpectedValue(value: number | null) {
      this._maximumExpectedValue = value === undefined ? null : value;
    }
    private _minimumExpectedValue: number | null = null;
    /**
    * The minimum value the metric is expected to have during an assembly.
    */
    public get minimumExpectedValue(): number | null { return this._minimumExpectedValue }
    public set minimumExpectedValue(value: number | null) {
      this._minimumExpectedValue = value === undefined ? null : value;
    }
    /**
    * Indicates whether process actions should be restricted if the latest QC test result fall outside the expected range.
    */
    public isProcessRestricted: boolean = false;
    private _expectedBooleanValue: boolean | null = null;
    /**
    * For boolean metrics, indicates whether the result is expected to be True or False.
    */
    public get expectedBooleanValue(): boolean | null { return this._expectedBooleanValue }
    public set expectedBooleanValue(value: boolean | null) {
      this._expectedBooleanValue = value === undefined ? null : value;
    }
    public readOnly: RecipeQcTestMetricExpectationReadOnly = new RecipeQcTestMetricExpectationReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.qcTestMetricLineId = Guid.parseOrEmpty(jsonSerializedObject.qcTestMetricLineId);
          this.expectedEndValue = jsonSerializedObject.expectedEndValue == null ? null : jsonSerializedObject.expectedEndValue;
          this.expectedStartValue = jsonSerializedObject.expectedStartValue == null ? null : jsonSerializedObject.expectedStartValue;
          this.maximumExpectedValue = jsonSerializedObject.maximumExpectedValue == null ? null : jsonSerializedObject.maximumExpectedValue;
          this.minimumExpectedValue = jsonSerializedObject.minimumExpectedValue == null ? null : jsonSerializedObject.minimumExpectedValue;
          this.isProcessRestricted = jsonSerializedObject.isProcessRestricted;
          this.expectedBooleanValue = jsonSerializedObject.expectedBooleanValue == null ? null : jsonSerializedObject.expectedBooleanValue;
          this.readOnly = new RecipeQcTestMetricExpectationReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.qcTestMetricLineId = this.qcTestMetricLineId.toString();
        toRet.expectedEndValue = this.expectedEndValue == null ? null : this.expectedEndValue;
        toRet.expectedStartValue = this.expectedStartValue == null ? null : this.expectedStartValue;
        toRet.maximumExpectedValue = this.maximumExpectedValue == null ? null : this.maximumExpectedValue;
        toRet.minimumExpectedValue = this.minimumExpectedValue == null ? null : this.minimumExpectedValue;
        toRet.isProcessRestricted = this.isProcessRestricted;
        toRet.expectedBooleanValue = this.expectedBooleanValue == null ? null : this.expectedBooleanValue;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "qcTestMetricLineId", rule: (v: any) => !v.isEmpty() || "RecipeQcTestMetricExpectation.QcTestMetricLineId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class RecipeQcTestHeaderReadOnly extends ModelObject<RecipeQcTestHeaderReadOnly> {

    /**
    * Either old-style Assembly RecipeId OR new-style Batch RecipeId which uses the given qc test.
    */
    public recipeId: Guid = Guid.createEmpty();
    /**
    * Default ordering sequence value.
    */
    public sequence: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.recipeId = Guid.parseOrEmpty(jsonSerializedObject.recipeId);
          this.sequence = jsonSerializedObject.sequence;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.recipeId = this.recipeId.toString();
        toRet.sequence = this.sequence;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "recipeId", rule: (v: any) => !v.isEmpty() || "RecipeQcTestHeaderReadOnly.RecipeId cannot be empty"},
            {field: "sequence", rule: (v: any) => v == null || v >= -2147483648 || "Sequence is not greater than minimum allowed value: -2147483648"},
            {field: "sequence", rule: (v: any) => v == null || v <= 2147483647 || "Sequence is above maximum allowed value: 2147483647"},
    ];
}


export class RecipeQcTestHeader extends ModelObject<RecipeQcTestHeader> {

    /**
    * Qc Test to be used in the recipe.
    */
    public qcTestId: Guid = Guid.createEmpty();
    public readOnly: RecipeQcTestHeaderReadOnly = new RecipeQcTestHeaderReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.qcTestId = Guid.parseOrEmpty(jsonSerializedObject.qcTestId);
          this.readOnly = new RecipeQcTestHeaderReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.qcTestId = this.qcTestId.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "qcTestId", rule: (v: any) => !v.isEmpty() || "RecipeQcTestHeader.QcTestId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class RecipeQcTest extends ModelObject<RecipeQcTest> {

    public header: RecipeQcTestHeader = new RecipeQcTestHeader();
    /**
    * The expectations which are defined for any metrics within the test definition. Note a metric with no expectations defined may have either an all-null entry or may be omitted from the array.
    */
    public metricExpectations: (RecipeQcTestMetricExpectation)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new RecipeQcTestHeader(jsonSerializedObject.header);
          this.metricExpectations = jsonSerializedObject.metricExpectations.map((v: any) => new RecipeQcTestMetricExpectation(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.metricExpectations = this.metricExpectations.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "metricExpectations", rule: (v: any) => !!v || "metricExpectations is required"},
            {field: "metricExpectations", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as RecipeQcTestMetricExpectation[]).reduce((acc: Array<string>, curr: RecipeQcTestMetricExpectation) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class RecipeQcTestService extends ServiceBase {

  public async getRecipeQcTestsForRecipe(recipeId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(RecipeQcTest)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    recipeId:recipeId.toString(),
    };
    const extractor = (response:any) => response.recipeQcTests.map((v: any) => new RecipeQcTest(v));;
    return this.makeJsonRequest("recipeQcTest/v1/getRecipeQcTestsForRecipe", req, false, extractor, opts);
  };

  public async saveFullSetOfRecipeQcTests(recipeId: Guid, recipeQcTests: readonly RecipeQcTest[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    recipeId:recipeId.toString(),
    recipeQcTests:recipeQcTests.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("recipeQcTest/v1/saveFullSetOfRecipeQcTests", req, true, extractor, opts);
  };

  public async hardDeleteRecipeQcTest(recipeId: Guid, qcTestId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    recipeId:recipeId.toString(),
    qcTestId:qcTestId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("recipeQcTest/v1/hardDeleteRecipeQcTest", req, true, extractor, opts);
  };

  public async addQcTestToRecipe(recipeId: Guid, qcTestId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    recipeId:recipeId.toString(),
    qcTestId:qcTestId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("recipeQcTest/v1/addQcTestToRecipe", req, true, extractor, opts);
  };

  public async updateRecipeQcTestMetricExpectations(recipeId: Guid, updates: readonly RecipeQcTestMetricExpectation[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    recipeId:recipeId.toString(),
    updates:updates.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("recipeQcTest/v1/updateRecipeQcTestMetricExpectations", req, true, extractor, opts);
  };

  public async setRecipeQcTestSequencesForRecipe(recipeId: Guid, qcTestIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    recipeId:recipeId.toString(),
    qcTestIds:qcTestIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("recipeQcTest/v1/setRecipeQcTestSequencesForRecipe", req, true, extractor, opts);
  };

  public async getRecipeQcTestsForRecipes(recipeIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(RecipeQcTest)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    recipeIds:recipeIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.recipeQcTests.map((v: any) => new RecipeQcTest(v));;
    return this.makeJsonRequest("recipeQcTest/v1/getRecipeQcTestsForRecipes", req, false, extractor, opts);
  };
}

export const recipeQcTestService = new RecipeQcTestService();